import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Tooltip, Popup } from "react-leaflet";
import L from "leaflet";
import { useMemo } from "react";
import { useTheme } from "@/lib/theme";
import { OSM_TILE_URL, OSM_ATTRIBUTION, tileClassForTheme } from "@/lib/tiles";

// Fix default marker icon paths (bundler strips them by default)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const waypointIcon = L.divIcon({
    html: `<div style="width:10px;height:10px;background:#F8FAFC;border:1.5px solid #E87722;border-radius:50%;box-shadow:0 0 0 1px #14161A"></div>`,
    className: "",
    iconSize: [10, 10],
    iconAnchor: [5, 5],
});

const riddenIcon = L.divIcon({
    html: `<div style="width:14px;height:14px;background:#E87722;border:2px solid #F8FAFC;border-radius:50%;box-shadow:0 0 0 1px #14161A,0 0 8px rgba(232,119,34,0.6)"></div>`,
    className: "",
    iconSize: [14, 14],
    iconAnchor: [7, 7],
});

const waypointKeyOf = (w) =>
    `${(w.name || "unnamed").trim()}@${Number(w.lat).toFixed(4)},${Number(w.lng).toFixed(4)}`;

export const RouteMap = ({ center, polyline, name, waypoints = [], height = "480px", ridden = null }) => {
    const { theme } = useTheme();
    const bounds = useMemo(() => {
        if (!polyline || polyline.length === 0) return null;
        const lats = polyline.map((p) => p[0]);
        const lngs = polyline.map((p) => p[1]);
        return [
            [Math.min(...lats), Math.min(...lngs)],
            [Math.max(...lats), Math.max(...lngs)],
        ];
    }, [polyline]);

    return (
        <div data-testid="route-map" className="w-full border border-[var(--border-hair)] overflow-hidden relative" style={{ height }}>
            <MapContainer
                center={center}
                zoom={9}
                bounds={bounds}
                boundsOptions={{ padding: [30, 30] }}
                scrollWheelZoom={false}
                style={{ height: "100%", width: "100%" }}
                className={`z-0 ${tileClassForTheme(theme)}`}
            >
                <TileLayer
                    url={OSM_TILE_URL}
                    attribution={OSM_ATTRIBUTION}
                    maxZoom={19}
                />
                <Polyline
                    positions={polyline}
                    pathOptions={{ color: "#E87722", weight: 4, opacity: 0.95 }}
                />
                {polyline.length > 0 && (
                    <>
                        <CircleMarker
                            center={polyline[0]}
                            pathOptions={{ color: "#F8FAFC", fillColor: "#E87722", fillOpacity: 1, weight: 2 }}
                            radius={7}
                        >
                            <Tooltip permanent direction="right" offset={[10, 0]} className="!bg-[var(--card-bg)] !text-[var(--fg)] !border !border-[#E87722] !rounded-none">START</Tooltip>
                        </CircleMarker>
                        <CircleMarker
                            center={polyline[polyline.length - 1]}
                            pathOptions={{ color: "#F8FAFC", fillColor: "#0A0A0A", fillOpacity: 1, weight: 2 }}
                            radius={7}
                        >
                            <Tooltip permanent direction="right" offset={[10, 0]} className="!bg-[var(--card-bg)] !text-[var(--fg)] !border !border-white/40 !rounded-none">FINISH</Tooltip>
                        </CircleMarker>
                    </>
                )}
                {waypoints.map((w, i) => {
                    const isDone = !!(ridden && ridden.isRidden && ridden.isRidden(waypointKeyOf(w)));
                    return (
                        <Marker key={`wpt-${i}`} position={[w.lat, w.lng]} icon={isDone ? riddenIcon : waypointIcon}>
                            <Popup className="waypoint-popup">
                                <div style={{ fontFamily: "Barlow Condensed, sans-serif", fontWeight: 900, textTransform: "uppercase", fontSize: 14, color: "#F8FAFC", letterSpacing: "0.02em" }}>{w.name}</div>
                                {w.sym && <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.2em", color: "#E87722", marginTop: 4 }}>{w.sym}</div>}
                                {w.desc && <div style={{ fontSize: 12, color: "#A1A1AA", marginTop: 6, whiteSpace: "pre-line" }}>{w.desc}</div>}
                                {isDone && <div style={{ marginTop: 8, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.24em", color: "#E87722", fontWeight: 700 }}>✓ Ridden</div>}
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>
        </div>
    );
};
